import { MouseEventHandler } from "react";
import { useRouter } from "next/router";

import { isNonEmptyArray } from "@enkor/toss-slash";

import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { CalendarIcon, LocationIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import AvatarList from "@domains/common/views/components/AvatarList/AvatarList";
import CategoryLabel from "@domains/common/views/components/CategoryLabel/CategoryLabel";
import WebViewBridge from "@logics/utils/webViewBridge";
import ProjectEventInfoViewModel from "@domains/project/viewModel/ProjectEventInfoViewModel";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";

import * as S from "./ProjectEventCard.style";

interface EventCardProps {
  projectId: string;
  eventId: number;
  host: ProjectEventInfoViewModel["host"];
  eventInfo: ProjectEventInfoViewModel;
  participantList: ProjectEventInfoViewModel["participantList"];
}

const ProjectEventCard = ({
  projectId,
  eventId,
  host,
  eventInfo,
  participantList,
}: EventCardProps) => {
  const { push, query } = useRouter();

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (WebViewBridge.isInAppWebView()) {
      event.preventDefault();
      WebViewBridge.getInstance().navigateToEventDetail(eventId);
    } else {
      push({
        pathname: NAVIGATION_ROUTE.LANDING_DETAIL.PATHNAME,
        query: { ...query, eventId },
      });
    }

    let EVENT_CARD_NAME = "CLICK_EVENT_CARD";
    if (eventInfo.isClosed) {
      EVENT_CARD_NAME = "CLICK_CLOSED_EVENT_CARD";
    } else if (eventInfo.isFull) {
      EVENT_CARD_NAME = "CLICK_FULL_EVENT_CARD";
    }

    dataTrackEvent({
      eventName: DATA_TRACKING_EVENT_NAME.PROJECT_DETAIL[EVENT_CARD_NAME],
      properties: {
        evenId: eventInfo.id,
        eventCategory: eventInfo.categoryList[0].name,
        eventTitle: eventInfo.title,
        eventImage: eventInfo.eventImage,
        eventDate: eventInfo.date,
        eventMaxParticipantCount: eventInfo.maxParticipantCount,
        eventParticipantCount: eventInfo.participantCount,
        projectId,
        host,
      },
    });
  };

  return (
    <S.EventCard
      href={{
        pathname: `${NAVIGATION_ROUTE.EVENT_DETAIL.HREF}/${eventId}`,
      }}
      $isWebview={WebViewBridge.isInAppWebView()}
      onClick={handleClick}
    >
      <S.EventInfoWrapper>
        <S.EventImageWrapper>
          <S.EventImage
            priority
            src={
              (eventInfo?.eventImage?.startsWith("https://")
                ? eventInfo?.eventImage
                : "") || "/assets/park-road.png"
            }
            alt="Event"
            width={0}
            height={0}
            sizes={`${S.EVENT_IMAGE_SIZE * 2}px`} // HACK: 이미지 사이즈와 동일하게 하면 가로로 긴 사진이 찌그러짐.
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />

          {!eventInfo.isClosed && eventInfo.isFull && (
            <S.StatusTag name="h5">Full</S.StatusTag>
          )}
        </S.EventImageWrapper>
        <S.EventInfo>
          {isNonEmptyArray(eventInfo.categoryList) &&
            eventInfo.categoryList.map(({ id }) => (
              <CategoryLabel key={id} type={id} />
            ))}
          <S.EventTitle name="h5">{eventInfo.title}</S.EventTitle>
          <S.EventDetails>
            <S.HostInfo>
              <S.AvatarImageWrapper>
                <S.HostAvatar
                  src={host.profileImage || "/assets/image_default9.png"}
                  alt="Host profile"
                  width={0}
                  height={0}
                  quality={50}
                  sizes={`${S.AVATAR_SIZE}px`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </S.AvatarImageWrapper>
              <S.HostName name="s">Hosted by {host.name}</S.HostName>
            </S.HostInfo>
            <S.EventDetailBox>
              <LocationIcon size={18} strokeColor={grayScale.white} />
              <S.EventDetail name="s">
                {eventInfo.location.nearStation}
              </S.EventDetail>
            </S.EventDetailBox>
            <S.EventDetailBox>
              <CalendarIcon size={18} strokeColor={grayScale.white} />
              <S.EventDetail name="s">{eventInfo.date}</S.EventDetail>
            </S.EventDetailBox>
          </S.EventDetails>
          <S.UserAvatars $isStrong={eventInfo.isFull}>
            <AvatarList
              spaceBetween
              withBorder
              avatarList={participantList}
              additionalText={`${eventInfo?.participantCount}/${eventInfo?.maxParticipantCount}`}
            />
          </S.UserAvatars>
        </S.EventInfo>
      </S.EventInfoWrapper>

      {eventInfo.isClosed && <S.CloseEventWrapper />}
    </S.EventCard>
  );
};

export default ProjectEventCard;
